import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Button, CircularProgress, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { get, isEmpty, omit } from "lodash";

import DatePicker from "Components/Common/DatePicker";
import InputFieldText from "Components/Common/InputFieldText";
import PaymentRequest from "Components/PaymentRequest";

import { dateFormatter } from "utils/dateFormatter";
import { onlyNumberKeys } from "utils";

import { usePostDemandEstimationPaymentMutation } from "apis/DemandEstimation";

const DemandEstimatePaymentModalForSezPremise = ({
  charges,
  id,
  handleClose,
  handleCloseForSubmit,
  isPowerUtility,
  t,
}) => {
  const [getEncryptionForPayment, { isLoading }] =
    usePostDemandEstimationPaymentMutation();

  const initialValues = useMemo(
    () => ({
      securityDeposit: charges?.securityDeposit ?? 0,

      serviceConnectionCharges: charges?.serviceConnectionCharges ?? 0,
      ...(charges?.serviceConnectionCharges > 0 && {
        serviceConnectionChargesCGST:
          charges?.serviceConnectionChargesCGST ?? 0,
        serviceConnectionChargesIGST:
          charges?.serviceConnectionChargesIGST ?? 0,
        serviceConnectionChargesSGST:
          charges?.serviceConnectionChargesSGST ?? 0,
        serviceConnectionChargesTds: 0,
        serviceConnectionChargesCGSTTds: 0,
        serviceConnectionChargesIGSTTds: 0,
        serviceConnectionChargesSGSTTds: 0,
        serviceConnectionChargesTdsYear: moment(),
        serviceConnectionChargesCGSTTdsYear: moment(),
        serviceConnectionChargesIGSTTdsYear: moment(),
        serviceConnectionChargesSGSTTdsYear: moment(),
      }),

      consumerEnergyMeterCharges: charges?.consumerEnergyMeterCharges ?? 0,
      ...(charges?.consumerEnergyMeterCharges > 0 && {
        cemChargeCGST: charges?.cemChargeCGST ?? 0,
        cemChargeIGST: charges?.cemChargeIGST ?? 0,
        cemChargeSGST: charges?.cemChargeSGST ?? 0,
        consumerEnergyMeterChargesTDS: 0,
        cemChargeCGSTTds: 0,
        cemChargeIGSTTds: 0,
        cemChargeSGSTTds: 0,
        consumerEnergyMeterChargesTDSYear: moment(),
        cemChargeCGSTTdsYear: moment(),
        cemChargeIGSTTdsYear: moment(),
        cemChargeSGSTTdsYear: moment(),
      }),

      settlementConnectionCharges: charges?.settlementConnectionCharges ?? 0,
      ...(charges?.settlementConnectionCharges > 0 && {
        settlementConnectionChargesTDS: 0,
        settlementConnectionChargesTDSYear: moment(),
      }),
    }),
    [charges]
  );

  const { isServiceConnectionCharges, isCemCharge, isSettlementCharges } =
    useMemo(
      () => ({
        isServiceConnectionCharges: charges?.serviceConnectionCharges > 0,
        isCemCharge: charges?.consumerEnergyMeterCharges > 0,
        isSettlementCharges: charges?.settlementConnectionCharges > 0,
      }),
      [charges]
    );

  const validationSchema = useMemo(
    () => ({
      ...(isCemCharge && {
        consumerEnergyMeterChargesTDS: Yup.number()
          .required("CEM Charges TDS amount is required")
          .test("isValidCemChargeTds", "", function () {
            const [{ value: current = null } = {}] = this.from || [];
            if (current["consumerEnergyMeterChargesTDS"] < 0) {
              return this.createError({
                message: "CEM Charges TDS Amount must greater than 0",
              });
            }

            if (
              current["consumerEnergyMeterCharges"] <
              current["consumerEnergyMeterChargesTDS"]
            ) {
              return this.createError({
                message: "CEM Charges TDS Amount must be less than CEM Charges",
              });
            }

            return true;
          }),
      }),
      ...(isServiceConnectionCharges && {
        serviceConnectionChargesTds: Yup.number()
          .required("Service connection charges Tds amount is required")
          .test("isValidServiceConnectionChargesTds", "", function () {
            const [{ value: current = null } = {}] = this.from || [];
            if (current["serviceConnectionChargesTds"] < 0) {
              return this.createError({
                message:
                  "Service Connection Charges TDS Amount must greater than 0",
              });
            }

            if (
              current["serviceConnectionCharges"] <
              current["serviceConnectionChargesTds"]
            ) {
              return this.createError({
                message:
                  "Service Connection Charges TDS Amount must be less than Service Connection Charges Charges",
              });
            }

            return true;
          }),
      }),
      ...(isSettlementCharges && {
        settlementConnectionChargesTDS: Yup.number()
          .required("Service connection charges Tds amount is required")
          .test("isValidSettlementConnectionChargesTds", "", function () {
            const [{ value: current = null } = {}] = this.from || [];
            if (current["settlementConnectionChargesTDS"] < 0) {
              return this.createError({
                message:
                  "Service Connection Charges TDS Amount must greater than 0",
              });
            }

            if (
              current["settlementConnectionCharges"] <
              current["settlementConnectionChargesTDS"]
            ) {
              return this.createError({
                message:
                  "Settlement Connection Charges TDS Amount must be less than Settlement Connection Charges Charges",
              });
            }

            return true;
          }),
      }),
    }),
    [isServiceConnectionCharges, isCemCharge, isSettlementCharges]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object(validationSchema),
  });

  const {
    values,
    errors,
    values: { securityDeposit },
  } = formik;

  const payNowBtnHandler = async () => {
    try {
      if (isEmpty(errors)) {
        const payload = omit(
          {
            id,
            ...values,
            ...(isServiceConnectionCharges && {
              serviceConnectionChargesCGSTTdsYear:
                values.serviceConnectionChargesCGSTTdsYear.format("YYYY"),
              serviceConnectionChargesIGSTTdsYear:
                values.serviceConnectionChargesIGSTTdsYear.format("YYYY"),
              serviceConnectionChargesSGSTTdsYear:
                values.serviceConnectionChargesSGSTTdsYear.format("YYYY"),
              serviceConnectionChargesTdsYear:
                values.serviceConnectionChargesSGSTTdsYear.format("YYYY"),
            }),
            ...(isCemCharge && {
              cemChargeCGSTTdsYear: values.cemChargeCGSTTdsYear.format("YYYY"),
              cemChargeIGSTTdsYear: values.cemChargeIGSTTdsYear.format("YYYY"),
              cemChargeSGSTTdsYear: values.cemChargeSGSTTdsYear.format("YYYY"),
              consumerEnergyMeterChargesTDSYear:
                values.consumerEnergyMeterChargesTDSYear.format("YYYY"),
            }),
            ...(isSettlementCharges && {
              settlementConnectionChargesTDSYear:
                values.settlementConnectionChargesTDSYear.format("YYYY"),
            }),
          },
          [...Object.keys(charges), "tdsYear"]
        );

        return await getEncryptionForPayment(payload);
      }
    } catch (error) {}
  };

  return (
    <Grid
      container
      rowSpacing={1}
      columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      className="mt-3"
    >
      {securityDeposit > 0 && (
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label="Security Deposit"
            type="number"
            formik={formik}
            name="securityDeposit"
            disabled
          />
        </Grid>
      )}
      {isServiceConnectionCharges && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges*"
              type="number"
              formik={formik}
              name="serviceConnectionCharges"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="serviceConnectionChargesTds"
              placeholder=""
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="serviceConnectionChargesTdsYear"
              formik={formik}
              label="Service Connection Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(
                get(values, "serviceConnectionChargesTdsYear")
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges CGST*"
              type="number"
              formik={formik}
              name="serviceConnectionChargesCGST"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges CGST Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="serviceConnectionChargesCGSTTds"
              placeholder="Enter Service Connection Charges CGST Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="serviceConnectionChargesCGSTTdsYear"
              formik={formik}
              label="Service Connection Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(
                get(values, "serviceConnectionChargesCGSTTdsYear")
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges IGST*"
              type="number"
              formik={formik}
              name="serviceConnectionChargesIGST"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges IGST Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="serviceConnectionChargesIGSTTds"
              placeholder="Enter Service Connection Charges IGST Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="serviceConnectionChargesIGSTTdsYear"
              formik={formik}
              label="Service Connection Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(
                get(values, "serviceConnectionChargesIGSTTdsYear")
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges SGST*"
              type="number"
              formik={formik}
              name="serviceConnectionChargesSGST"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Service Connection Charges SGST Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="serviceConnectionChargesSGSTTds"
              placeholder="Enter Service Connection Charges SGST Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="serviceConnectionChargesSGSTTdsYear"
              formik={formik}
              label="Service Connection Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(
                get(values, "serviceConnectionChargesSGSTTdsYear")
              )}
            />
          </Grid>
        </>
      )}

      {isCemCharge && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Consumer Energy Meter Charges*"
              type="number"
              formik={formik}
              name="consumerEnergyMeterCharges"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Consumer Energy Meter Charges Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="consumerEnergyMeterChargesTDS"
              placeholder="Enter Consumer Energy Meter Charges Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="consumerEnergyMeterChargesTDSYear"
              formik={formik}
              label="CEM Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(
                get(values, "consumerEnergyMeterChargesTDSYear")
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="CEM Charges CGST*"
              type="number"
              formik={formik}
              name="cemChargeCGST"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="CEM Charges CGST Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="cemChargeCGSTTds"
              placeholder="Enter CEM Charges CGST Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="cemChargeCGSTTdsYear"
              formik={formik}
              label="CEM Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(get(values, "cemChargeCGSTTdsYear"))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="CEM Charges IGST*"
              type="number"
              formik={formik}
              name="cemChargeIGST"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="CEM Charges IGST Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="cemChargeIGSTTds"
              placeholder="Enter CEM Charges IGST Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="cemChargeIGSTTdsYear"
              formik={formik}
              label="CEM Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(get(values, "cemChargeIGSTTdsYear"))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="CEM Charges SGST*"
              type="number"
              formik={formik}
              name="cemChargeSGST"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="CEM Charges SGST Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="cemChargeSGSTTds"
              placeholder="Enter CEM Charges SGST Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="cemChargeSGSTTdsYear"
              formik={formik}
              label="CEM Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(get(values, "cemChargeSGSTTdsYear"))}
            />
          </Grid>
        </>
      )}

      {isSettlementCharges && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Settlement Connection Charges*"
              type="number"
              formik={formik}
              name="settlementConnectionCharges"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InputFieldText
              label="Settlement Connection Charges Tds Amount*"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              formik={formik}
              name="settlementConnectionChargesTDS"
              placeholder="Enter Settlement Connection Charges Tds Amount"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              name="settlementConnectionChargesTDSYear"
              formik={formik}
              label="Settlement Connection Charges Tds Financial Year*"
              views={["year"]}
              format={dateFormatter(
                get(values, "settlementConnectionChargesTDSYear")
              )}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} className="text-end mt-3">
        <PaymentRequest
          handleClose={() => {
            handleClose();
            handleCloseForSubmit();
          }}
          generateEncryption={payNowBtnHandler}
          isPowerUtility={isPowerUtility}
        >
          <Button disabled={isLoading} variant="contained">
            {t("Pay Now")} {isLoading && <CircularProgress size={20} />}
          </Button>
        </PaymentRequest>
      </Grid>
    </Grid>
  );
};

DemandEstimatePaymentModalForSezPremise.propTypes = {
  charges: PropTypes.object,
  handleClose: PropTypes.func,
  isPowerUtility: PropTypes.bool,
  id: PropTypes.number,
  handleCloseForSubmit: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(DemandEstimatePaymentModalForSezPremise);
